import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  Edit,
  Mail,
  CreditCard,
  Phone,
  CheckCircle,
  FileText,
  Percent,
  Truck,
  FileSpreadsheet,
  CircleDollarSign,
  Printer,
  XCircle,
  RotateCcw,
  Copy,
} from "lucide-react"
import axios from "axios"
import { ClipLoader } from "react-spinners"
import bag from "../../assets/icons/bag.png"
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet"
import "leaflet/dist/leaflet.css"
import L from "leaflet"

export default function OrderDetailsPage() {
  const { id } = useParams()
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480)
  const [orderDetails, setOrderDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(null)
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false)
  const [returnReason, setReturnReason] = useState("")
  const [returnError, setReturnError] = useState("")
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [courierService, setCourierService] = useState("")
  const [confirmError, setConfirmError] = useState("")
  const [copiedLinkIndex, setCopiedLinkIndex] = useState(null)

  const navigate = useNavigate()

  const defaultIcon = L.icon({
    iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
    iconRetinaUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
    shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  })

  const warehouseCoords = [32.082466, 72.669128]

  function OrderMap({ city }) {
    const [customerCoords, setCustomerCoords] = useState([32.0837, 72.6711])
    const [mapLoading, setMapLoading] = useState(true)
    const [mapError, setMapError] = useState(null)

    useEffect(() => {
      const fetchCoordinates = async () => {
        if (!city) {
          setMapError("No city provided")
          setMapLoading(false)
          return
        }

        try {
          setMapLoading(true)
          const encodedCity = encodeURIComponent(`${city}, Pakistan`)
          const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodedCity}`)
          const data = await response.json()

          if (data.length > 0) {
            const lat = Number.parseFloat(data[0].lat)
            const lon = Number.parseFloat(data[0].lon)
            setCustomerCoords([lat, lon])
            setMapError(null)
          } else {
            setMapError("No results found for the city")
            setCustomerCoords([32.0837, 72.6711])
          }
        } catch (err) {
          setMapError("Error fetching location")
          setCustomerCoords([32.0837, 72.6711])
          console.error("Error fetching coordinates:", err)
        } finally {
          setMapLoading(false)
        }
      }

      fetchCoordinates()
    }, [city])

    const routeCoords = [warehouseCoords, customerCoords]

    const openGoogleMaps = (coords, isWarehouse = false) => {
      const [lat, lng] = coords
      const label = isWarehouse ? "Our Warehouse" : `Delivery Location: ${city}`
      const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`
      window.open(url, "_blank")
    }

    if (mapLoading) {
      return (
        <div
          style={{
            width: "100%",
            height: "440px",
            borderRadius: "10px",
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#333",
          }}
        >
          <ClipLoader color="#D4AF37" size={50} speedMultiplier={1} />
        </div>
      )
    }

    if (mapError) {
      return (
        <div
          style={{
            width: "100%",
            height: "440px",
            borderRadius: "10px",
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#333",
          }}
        >
          <p style={{ color: "#E5D0A5", fontSize: "14px" }}>{mapError}</p>
        </div>
      )
    }

    return (
      <div
        style={{
          width: "100%",
          height: "440px",
          borderRadius: "10px",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <MapContainer
          center={warehouseCoords}
          zoom={7}
          style={{ height: "100%", width: "100%" }}
          attributionControl={false}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Marker
            position={warehouseCoords}
            icon={defaultIcon}
            eventHandlers={{ click: () => openGoogleMaps(warehouseCoords, true) }}
          >
            <Popup>Our Warehouse</Popup>
          </Marker>
          <Marker
            position={customerCoords}
            icon={defaultIcon}
            eventHandlers={{ click: () => openGoogleMaps(customerCoords, false) }}
          >
            <Popup>Delivery Location: {city}</Popup>
          </Marker>
          <Polyline positions={routeCoords} color="#D4AF37" weight={3} dashArray="5, 5" />
        </MapContainer>
        <div style={styles.mapOverlay}>
          <Truck style={styles.mapOverlayIcon} size={14} />
          <span>From Warehouse to {city}</span>
        </div>
      </div>
    )
  }

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
      setIsSmallMobile(window.innerWidth <= 480)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const fetchOrderDetails = async ({ silent = false } = {}) => {
    if (!id) return

    try {
      if (!silent) setLoading(true)
      const response = await axios.get(`https://api.alburaqscents.com/v1/order/detail/${id}`)
      setOrderDetails(response.data.data)
    } catch (error) {
      console.error("Error fetching order details:", error)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  const updateOrderStatus = async (
    newStatus,
    returnComment = "",
    courierService = "",
    actionKey = newStatus
  ) => {
    if (!orderDetails?._id || actionLoading) return

    try {
      setActionLoading(actionKey)
      const payload = { status: newStatus }
      if (returnComment) payload.returnComment = returnComment
      if (courierService) payload.courierService = courierService
      await axios.patch(`https://api.alburaqscents.com/v1/order/${orderDetails._id}`, payload)
      if (newStatus === "confirmed") {
        const requestOptions = {
          method: "POST",
          redirect: "follow",
        }

        await fetch(
          `https://api.alburaqscents.com/v1/order/send-confirmation/${orderDetails._id}`,
          requestOptions
        )
          .then((response) => response.text())
          .then((result) => console.log("Confirmation sent:", result))
          .catch((error) => console.error("Error sending confirmation:", error))
      }
      await fetchOrderDetails({ silent: true })
      return true
    } catch (error) {
      console.error("Error updating order status:", error)
      setReturnError("Failed to update order status. Please try again.")
      return false
    } finally {
      setActionLoading(null)
    }
  }

  const isStatusActive = (targetStatus) => {
    const statusOrder = ["pending", "confirmed", "processing", "outForDelivery", "delivered"]
    const currentIndex = statusOrder.indexOf(orderDetails?.orderStatus)
    const targetIndex = statusOrder.indexOf(targetStatus)
    return currentIndex >= targetIndex
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "#FFA500"
      case "confirmed":
        return "#2196F3"
      case "processing":
        return "#FF9800"
      case "outForDelivery":
        return "#9C27B0"
      case "delivered":
        return "#4CAF50"
      case "cancelled":
        return "#F44336"
      case "returned":
        return "#F44336"
      default:
        return "#9E9E9E"
    }
  }

  const formatStatus = (status) => {
    switch (status) {
      case "pending":
        return "Pending"
      case "confirmed":
        return "Confirmed"
      case "processing":
        return "Processing"
      case "outForDelivery":
        return "Out for Delivery"
      case "delivered":
        return "Delivered"
      case "cancelled":
        return "Cancelled"
      case "returned":
        return "Returned"
      default:
        return status.charAt(0).toUpperCase() + status.slice(1)
    }
  }

  const calculateSubTotal = () => {
    if (!orderDetails?.items) return 0
    return orderDetails.items.reduce((total, item) => {
      return total + item.quantity * item.priceAtPurchase
    }, 0)
  }

  const handleCancelOrder = async () => {
    if (window.confirm("Are you sure you want to cancel this order?")) {
      await updateOrderStatus("cancelled")
    }
  }

  const handleReturnSubmit = async () => {
    if (!returnReason.trim()) {
      setReturnError("Please provide a return reason.")
      return
    }
    const ok = await updateOrderStatus("returned", returnReason, "", "returned")
    if (ok) {
      setIsReturnModalOpen(false)
      setReturnReason("")
      setReturnError("")
    }
  }

  const handleConfirmSubmit = async () => {
    if (!courierService.trim()) {
      setConfirmError("Please select a courier service.")
      return
    }
    const ok = await updateOrderStatus("confirmed", "", courierService, "confirmed")
    if (ok) {
      setIsConfirmModalOpen(false)
      setCourierService("")
      setConfirmError("")
    }
  }

  const renderActionLabel = (key, label, loaderColor = "#000") =>
    actionLoading === key ? (
      <ClipLoader color={loaderColor} size={18} speedMultiplier={0.9} />
    ) : (
      label
    )

  // Copy link to clipboard
  const copyToClipboard = async (link, index) => {
    try {
      await navigator.clipboard.writeText(link)
      setCopiedLinkIndex(index)
      setTimeout(() => setCopiedLinkIndex(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  useEffect(() => {
    fetchOrderDetails()
  }, [id])

  const handlePrint = async () => {
    if (actionLoading) return
    try {
      setActionLoading("print")
      const response = await axios.get(`https://api.alburaqscents.com/v1/order/detail/${id}`)
      const printData = response.data.data
      console.log("[v0] Opening print window")
      const printWindow = window.open("", "_blank")

      if (!printWindow) {
        alert("Please allow popups for this site to enable printing")
        return
      }

      console.log("[v0] Generating print HTML")
      const printHTML = generatePrintHTML(printData)
      printWindow.document.write(printHTML)
      printWindow.document.close()

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print()
          printWindow.close()
        }, 500)
      }
    } catch (error) {
      console.error("[v0] Error printing order:", error)
      alert(`Failed to print order: ${error.message}. Please try again.`)
    } finally {
      setActionLoading(null)
    }
  }

const generatePrintHTML = (orderData) => {
    const currentDate = new Date().toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 10px; 
            line-height: 1.2; 
            color: #000; 
            background: white; 
            margin: 0;
            padding: 0;
          }
          .print-container { 
            width: 100%; 
            max-width: 1000px; 
            margin: 0 auto; 
            padding: 10px;
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
          .header-section { 
            display: flex; 
            justify-content: space-between; 
            align-items: center;
            border-bottom: 2px solid #000;
            padding-bottom: 8px;
            margin-bottom: 5px;
          }
          .company-info { 
            text-align: left;
          }
          .company-name { 
            font-size: 16px; 
            font-weight: bold; 
          }
          .order-info { 
            text-align: right;
            font-size: 9px;
          }
          .main-content { 
            display: flex; 
            gap: 15px;
            min-height: 300px;
          }
          .left-column { 
            flex: 1; 
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
          .right-column { 
            flex: 1; 
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
          .info-box { 
            border: 1px solid #000; 
            padding: 6px;
            border-radius: 3px;
          }
          .box-title { 
            font-size: 9px; 
            font-weight: bold; 
            margin-bottom: 3px;
            border-bottom: 1px solid #ccc;
            padding-bottom: 2px;
          }
          .items-table { 
            width: 100%; 
            border-collapse: collapse; 
            font-size: 8px;
            margin-top: 5px;
          }
          .items-table th, .items-table td { 
            border: 1px solid #000; 
            padding: 3px; 
            text-align: left;
          }
          .items-table th { 
            background: #f0f0f0; 
            font-weight: bold;
            font-size: 8px;
          }
          .courier-instructions { 
            border: 1px solid #000;
            padding: 6px;
            margin-top: 5px;
            font-size: 9px;
          }
          .payment-highlight {
            font-weight: bold;
            color: #d00;
            background: #fff0f0;
            padding: 2px 4px;
            border-radius: 2px;
          }
          .barcode-area {
            text-align: center;
            margin-top: 5px;
            padding: 5px;
            border: 1px dashed #ccc;
          }
          .signature-area { 
            display: flex; 
            justify-content: space-between; 
            margin-top: 10px;
            font-size: 8px;
          }
          .signature-box { 
            width: 120px; 
            text-align: center;
          }
          .signature-line { 
            border-bottom: 1px solid #000; 
            height: 25px; 
            margin-bottom: 2px;
          }
          .urgent-stamp {
            position: absolute;
            top: 50px;
            right: 50px;
            border: 2px solid red;
            padding: 5px 10px;
            transform: rotate(15deg);
            color: red;
            font-weight: bold;
            background: white;
          }
          
          /* Print-specific styles */
          @page {
            size: landscape;
            margin: 0.5cm;
          }
          @media print {
            body { 
              margin: 0; 
              padding: 0; 
              -webkit-print-color-adjust: exact; 
              print-color-adjust: exact;
              width: 100%;
            }
            .print-container { 
              margin: 0; 
              padding: 10px; 
              width: 100%;
              max-width: none;
            }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="print-container">
          <!-- Header Section -->
          <div class="header-section">
            <div class="company-info">
              <div class="company-name">ALBURAQ SCENTS</div>
              <div style="font-size: 9px;">Premium Fragrance Collection</div>
            </div>
            <div class="order-info">
              <strong>ORDER #: ${orderData.orderNumber}</strong><br>
              Date: ${new Date(orderData.createdAt).toLocaleDateString()}<br>
              Print: ${currentDate}
            </div>
          </div>

          <!-- Main Content - Two Columns -->
          <div class="main-content">
            <!-- Left Column -->
            <div class="left-column">
              <!-- Shipping Information -->
              <div class="info-box">
                <div class="box-title">SHIP TO</div>
                <strong>${orderData.customerId?.name || "N/A"}</strong><br>
                📞 ${orderData.shippingAddress?.phoneNumber || "N/A"}<br>
                📧 ${orderData.customerId?.email || "N/A"}<br><br>
                <strong>Address:</strong><br>
                ${orderData.shippingAddress?.address || "N/A"}<br>
                ${orderData.shippingAddress?.city || ""}, ${orderData.shippingAddress?.state || ""}<br>
                ${orderData.shippingAddress?.country || ""}<br>
                ${orderData.shippingAddress?.famousSpot ? `<strong>Landmark:</strong> ${orderData.shippingAddress.famousSpot}` : ""}
              </div>

              <!-- Order Items -->
              <div class="info-box">
                <div class="box-title">ORDER ITEMS (${orderData.items?.length || 0})</div>
                <table class="items-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Size</th>
                      <th>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${orderData.items?.map((item) => {
                      const selectedSize = item.productId?.sizes?.find(
                        size => size._id === item.selectedSizeId
                      );
                      return `
                        <tr>
                          <td>${item.productId?.name || "N/A"}</td>
                          <td>${selectedSize?.sku || "N/A"}</td>
                          <td>${selectedSize?.ml ? selectedSize.ml + "ML" : "N/A"}</td>
                          <td>${item.quantity}</td>
                        </tr>
                      `;
                    }).join("") || '<tr><td colspan="4">No items</td></tr>'}
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Right Column -->
            <div class="right-column">
              <!-- Payment & Courier Info -->
              <div class="info-box">
                <div class="box-title">PAYMENT & DELIVERY</div>
                <div class="payment-highlight">
                  ${orderData.paymentMethod === "COD" 
                    ? "💰 CASH ON DELIVERY - COLLECT: PKR " + orderData.totalAmount?.toFixed(2) 
                    : "✅ PREPAID - NO COLLECTION REQUIRED"}
                </div>
                <br>
                <strong>Status:</strong> ${orderData.orderStatus?.toUpperCase()}<br>
                <strong>Amount:</strong> PKR ${orderData.totalAmount?.toFixed(2)}<br>
                <strong>Items:</strong> ${orderData.items?.length || 0} product(s)<br>
                <strong>Total Qty:</strong> ${orderData.items?.reduce((sum, item) => sum + item.quantity, 0) || 0}
              </div>

              <!-- Courier Instructions -->
              <div class="courier-instructions">
                <div class="box-title">COURIER INSTRUCTIONS</div>
                • 📞 <strong>CONTACT BEFORE DELIVERY</strong><br>
                • 🎁 Handle with care - Fragile items<br>
                • 📍 Verify address with recipient<br>
                • ⏰ Deliver during business hours<br>
                • 📝 Get signature upon delivery
              </div>

              <!-- Barcode Area -->
              <div class="barcode-area">
                <div style="font-size: 8px; margin-bottom: 3px;">TRACKING #: ${orderData.orderNumber}</div>
                <div style="font-family: 'Barcode'; font-size: 24px; letter-spacing: 2px;">
                  *${orderData.orderNumber}*
                </div>
                <div style="font-size: 7px; margin-top: 2px;">Scan above barcode for tracking</div>
              </div>
            </div>
          </div>

          <!-- Footer Signatures -->
         

          <!-- Urgent Stamp for Priority Orders -->
          ${orderData.orderStatus === 'processing' ? `
            <div class="urgent-stamp">
              URGENT DELIVERY
            </div>
          ` : ''}
        </div>
      </body>
      </html>
    `;
};

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100%",
          backgroundColor: "#E5D0A5",
        }}
      >
        <ClipLoader color="#000000" size={50} speedMultiplier={1} />
      </div>
    )
  }

  if (!orderDetails) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          width: "100%",
          backgroundColor: "#E5D0A5",
          textAlign: "center",
        }}
      >
        <div
          style={{
            position: "relative",
            width: "80px",
            height: "80px",
            marginBottom: "20px",
          }}
        >
          <img
            src={bag || "/placeholder.svg"}
            alt="Shopping bag"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              filter: "brightness(0) invert(1)",
            }}
          />
        </div>
        <div
          style={{
            color: "#000",
            fontSize: "15px",
            marginTop: "10px",
          }}
        >
          No Orders Yet
        </div>
      </div>
    )
  }

  const subTotal = calculateSubTotal()
  const discount = 0
  const deliveryCharges = 0
  const taxRate = 0.155
  const estimatedTax = subTotal * taxRate


  const progressItems = [
    { label: "Order Pending", status: "pending" },
    { label: "Confirmed", status: "confirmed" },
    { label: "Processing", status: "processing" },
    { label: "Out for Delivery", status: "outForDelivery" },
    { label: "Delivered", status: "delivered" },
  ]


  const styles = {
    mainWrapper: {
      justifyContent: "center",
      minHeight: "100vh",
      width: "100%",
      backgroundColor: "#E5D0A5",
      padding: isSmallMobile ? "10px" : isMobile ? "20px" : "40px",
      boxSizing: "border-box",
      fontFamily: "Segoe UI, Arial, sans-serif",
      overflowY: "auto",
      overflowX: "hidden",
    },
    pageTitle: {
      fontSize: "17px",
      fontWeight: "bold",
      color: "#000",
      marginBottom: "20px",
      width: "100%",
      paddingTop: "20px",
    },
    pageContent: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
      gap: isSmallMobile ? "15px" : "25px",
      width: "100%",
      maxWidth: "1200px",
    },
    column: {
      display: "flex",
      flexDirection: "column",
      gap: isSmallMobile ? "15px" : "25px",
    },
    card: {
      backgroundColor: "#000",
      borderRadius: "10px",
      padding: isSmallMobile ? "15px" : "20px",
      boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
      color: "#E5D0A5",
    },
    cardHeader: {
      fontSize: isSmallMobile ? "16px" : "20px",
      fontWeight: "bold",
      marginBottom: "15px",
      color: "#E5D0A5",
    },
    orderDetailsTop: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "10px",
      flexWrap: "wrap",
      gap: "10px",
    },
    orderId: {
      fontSize: isSmallMobile ? "18px" : "22px",
      fontWeight: "bold",
      color: "#E5D0A5",
      margin: 0,
    },
    statusPill: {
      color: "#000",
      padding: "4px 10px",
      borderRadius: "15px",
      fontSize: "12px",
      fontWeight: "bold",
      display: "inline-block",
    },
    paidPill: {
      color: "#FFF",
      padding: "2px 8px",
      borderRadius: "10px",
      fontSize: "10px",
      fontWeight: "bold",
      display: "inline-block",
      marginRight: "5px",
    },
    orderDate: {
      fontSize: "13px",
      color: "#BBB",
      flexBasis: "100%",
      marginTop: "5px",
    },
    buttonGroup: {
      display: "flex",
      gap: "10px",
      marginTop: "15px",
      flexWrap: "wrap",
      justifyContent: isMobile ? "center" : "flex-start",
    },
    button: {
      backgroundColor: "#333",
      color: "#E5D0A5",
      border: "1px solid #D4AF37",
      borderRadius: "5px",
      padding: "8px 12px",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: "bold",
      transition: "background-color 0.2s ease, border-color 0.2s ease",
      flexGrow: isMobile ? 1 : 0,
      maxWidth: isMobile ? "none" : "150px",
      textAlign: "center",
      whiteSpace: "nowrap",
    },
    cancelButton: {
      backgroundColor: "#F44336",
      color: "#FFF",
      border: "none",
    },
    returnButton: {
      backgroundColor: "transparent",
      borderColor: "#E5D0A5",
      color: "#E5D0A5",
    },
    subHeader: {
      fontSize: "14px",
      color: "#BBB",
      marginBottom: "10px",
      marginTop: "20px",
      fontWeight: "normal",
    },
    progressGrid: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: "10px",
      marginTop: "15px",
      alignItems: "center",
      overflowX: "auto",
      paddingBottom: "10px",
    },
    progressRowContainer: {
      display: "flex",
      alignItems: "center",
      width: "100%",
    },
    progressLabel: {
      fontSize: "14px",
      color: "#E5D0A5",
      fontWeight: "normal",
      flexShrink: 0,
      marginRight: "10px",
      minWidth: "80px",
    },
    progressBarTrack: {
      flexGrow: 1,
      height: "8px",
      backgroundColor: "#333",
      borderRadius: "4px",
      overflow: "hidden",
    },
    progressBarFill: {
      height: "100%",
      borderRadius: "4px",
      transition: "width 0.3s ease",
    },
    estimatedShipping: {
      marginTop: "20px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "10px",
    },
    estimatedShippingText: {
      fontSize: "13px",
      color: "#BBB",
      margin: 0,
    },
    makeReadyButton: {
      backgroundColor: "#D4AF37",
      color: "#000",
      border: "none",
      padding: "8px 15px",
      borderRadius: "5px",
      fontSize: "13px",
      fontWeight: "bold",
      cursor: "pointer",
      transition: "background-color 0.2s ease",
      whiteSpace: "nowrap",
    },
    productTableWrapper: {
      marginTop: "15px",
      borderRadius: "8px",
      overflowX: "auto",
      paddingBottom: "17px",
      marginBottom: "-17px",
    },
    productTable: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: "600px",
    },
    productTableHeader: {
      backgroundColor: "#1a1a1a",
      color: "#E5D0A5",
      fontSize: "12px",
      textAlign: "left",
      fontWeight: "bold",
    },
    productTh: {
      padding: "12px 8px",
      whiteSpace: "nowrap",
      borderBottom: "1px solid #333",
    },
    productRow: {
      borderBottom: "1px solid #333",
    },
    productTd: {
      padding: "10px 8px",
      verticalAlign: "middle",
      whiteSpace: "nowrap",
      fontSize: "12px",
      color: "#E5D0A5",
      fontWeight: "normal",
    },
    productIconContainer: {
      width: "24px",
      height: "24px",
      borderRadius: "5px",
      backgroundColor: "#D4AF37",
      display: "inline-flex",
      justifyContent: "center",
      alignItems: "center",
      marginRight: "8px",
      flexShrink: 0,
      overflow: "hidden",
    },
    productIconImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    orderSummaryRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "8px",
      fontSize: "14px",
      color: "#E5D0A5",
    },
    orderSummaryLabel: {
      color: "#E5D0A5",
      fontWeight: "normal",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    orderSummaryValue: {
      fontWeight: "normal",
      color: "#E5D0A5",
    },
    orderSummaryDivider: {
      borderBottom: "1px solid #333",
      margin: "10px 0",
    },
    totalAmount: {
      fontSize: "15px",
      fontWeight: "normal",
      color: "#E5D0A5",
      textAlign: "right",
    },
    totalAmountValue: {
      fontWeight: "normal",
      color: "#E5D0A5",
      fontSize: "15px",
    },
    orderSummaryIcon: {
      color: "#D4AF37",
      fontSize: "16px",
    },
    paymentInfoCardHeader: {
      fontSize: "17px",
      fontWeight: "bold",
      color: "#E5D0A5",
      marginBottom: "15px",
    },
    paymentInfoDivider: {
      borderBottom: "1px solid #333",
      margin: "0 0 15px 0",
    },
    paymentInfoRow: {
      display: "flex",
      alignItems: "center",
      marginBottom: "12px",
      fontSize: "15px",
      color: "#E5D0A5",
      flexWrap: "wrap",
    },
    paymentIconCircle: {
      width: "40px",
      height: "40px",
      borderRadius: "50%",
      backgroundColor: "#1a1a1a",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      marginRight: "12px",
      flexShrink: 0,
    },
    paymentInfoIcon: {
      color: "#D4AF37",
      fontSize: "17px",
    },
    paymentInfoLabel: {
      color: "#E5D0A5",
      marginRight: "5px",
      fontWeight: "normal",
    },
    paymentInfoValue: {
      fontWeight: "normal",
      color: "#E5D0A5",
      flexGrow: 1,
    },
    paymentInfoValueBold: {
      fontWeight: "bold",
      color: "#E5D0A5",
    },
    verifiedIcon: {
      color: "#4CAF50",
      marginLeft: "auto",
      fontSize: "17px",
      flexShrink: 0,
      alignSelf: "center",
    },
    addressCardText: {
      fontSize: "13px",
      color: "#BBB",
      lineHeight: "1.5",
      marginBottom: "8px",
      position: "relative",
      paddingRight: "25px",
      fontWeight: "normal",
      display: "flex",
      alignItems: "center",
    },
    addressEditIcon: {
      position: "absolute",
      right: "0",
      top: "2px",
      color: "#D4AF37",
      cursor: "pointer",
      fontSize: "14px",
    },
    mapContainer: {
      width: "100%",
      height: "200px",
      borderRadius: "10px",
      overflow: "hidden",
      position: "relative",
      backgroundColor: "#000",
      padding: "0",
    },
    mapFrame: {
      width: "100%",
      height: "100%",
      border: "none",
      borderRadius: "10px",
    },
    mapOverlay: {
      position: "absolute",
      bottom: "10px",
      left: "10px",
      backgroundColor: "rgba(0,0,0,0.7)",
      borderRadius: "5px",
      padding: "5px 10px",
      color: "#E5D0A5",
      fontSize: "12px",
      display: "flex",
      alignItems: "center",
      gap: "5px",
    },
    mapOverlayIcon: {
      color: "#4CAF50",
      fontSize: "14px",
    },
    fallbackContainer: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "#333",
      color: "#E5D0A5",
      textAlign: "center",
      padding: "20px",
    },
    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.7)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
    modalContent: {
      backgroundColor: "#000",
      borderRadius: "10px",
      padding: "20px",
      width: isMobile ? "90%" : "400px",
      maxWidth: "500px",
      color: "#E5D0A5",
      position: "relative",
    },
    modalHeader: {
      fontSize: "15px",
      fontWeight: "bold",
      marginBottom: "15px",
    },
    modalClose: {
      position: "absolute",
      top: "10px",
      right: "10px",
      cursor: "pointer",
      color: "#D4AF37",
    },
    modalInput: {
      width: "100%",
      padding: "10px",
      marginBottom: "10px",
      borderRadius: "5px",
      border: "1px solid #D4AF37",
      backgroundColor: "#333",
      color: "#E5D0A5",
      fontSize: "14px",
    },
    modalError: {
      color: "#F44336",
      fontSize: "12px",
      marginBottom: "10px",
    },
    modalButton: {
      backgroundColor: "#D4AF37",
      color: "#000",
      border: "none",
      padding: "8px 15px",
      borderRadius: "5px",
      fontSize: "13px",
      fontWeight: "bold",
      cursor: "pointer",
      width: "100%",
    },
  }



  return (
    <div style={styles.mainWrapper}>
      <h1 style={styles.pageTitle}>Order Details</h1>
      <div style={styles.pageContent}>
        <div style={styles.column}>
          <div style={styles.card}>
            <div style={styles.orderDetailsTop}>
              <h2 style={styles.orderId}>#{orderDetails.orderNumber || "Unknown"}</h2>
              <span
                style={{
                  ...styles.paidPill,
                  backgroundColor: orderDetails.paymentMethod === "COD" ? "#FFA500" : "#4CAF50",
                }}
              >
                {orderDetails.paymentMethod === "COD" ? "Cash on Delivery" : "Paid"}
              </span>
              <span
                style={{
                  ...styles.statusPill,
                  backgroundColor: getStatusColor(orderDetails.orderStatus),
                }}
              >
                {formatStatus(orderDetails.orderStatus)}
              </span>
            </div>
            <p style={styles.orderDate}>
              Order / Order Details/ #{orderDetails._id?.slice(-7) || "Unknown"} -
              {new Date(orderDetails.createdAt).toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
            {orderDetails.returnComment && (
              <p style={{ ...styles.addressCardText, color: "#F44336" }}>
                <span style={{ marginRight: "5px" }}>Return Reason:</span>
                {orderDetails.returnComment}
              </p>
            )}
            <div style={styles.buttonGroup}>
              <button
                onClick={() => navigate(`/edit-order/${orderDetails._id}`)}
                style={styles.button}
                disabled={!!actionLoading || orderDetails.orderStatus === "delivered" || orderDetails.orderStatus === "cancelled" || orderDetails.orderStatus === "returned"}
              >
                Edit Order
              </button>
              <button
                onClick={handlePrint}
                style={{ ...styles.button, opacity: actionLoading ? 0.7 : 1 }}
                disabled={!!actionLoading}
              >
                {renderActionLabel("print", "Print", "#D4AF37")}
              </button>
              {orderDetails.orderStatus !== "cancelled" && orderDetails.orderStatus !== "returned" && orderDetails.orderStatus !== "delivered" && (
                <button
                  onClick={handleCancelOrder}
                  style={{ ...styles.button, ...styles.cancelButton, opacity: actionLoading ? 0.7 : 1 }}
                  disabled={!!actionLoading}
                >
                  {renderActionLabel("cancelled", "Cancel Order", "#fff")}
                </button>
              )}
              {orderDetails.orderStatus !== "pending" && orderDetails.orderStatus !== "cancelled" && orderDetails.orderStatus !== "returned" && (
                <button
                  onClick={() => setIsReturnModalOpen(true)}
                  style={styles.button}
                  disabled={!!actionLoading}
                >
                  Return Order
                </button>
              )}
            </div>
            <h3 style={styles.subHeader}>Progress</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {progressItems.map((item, index) => {
                const isActive = isStatusActive(item.status);
                const fillWidth = isActive ? '100%' : index === 0 ? '0%' : '0%';
                const statusColor = getStatusColor(item.status);

                return (
                  <div key={item.status} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '14px', marginBottom: '5px' }}>{item.label}</span>
                    <div style={{ width: '100%', height: '10px', backgroundColor: '#ccc', borderRadius: '5px' }}>
                      <div style={{ ...styles.progressBarFill, width: fillWidth, backgroundColor: statusColor }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={styles.estimatedShipping}>
              <p style={styles.estimatedShippingText}>
                {orderDetails.orderStatus === "delivered" ? "Delivered on:" : "Estimated delivery:"}
                <span style={{ color: "#D4AF37", fontWeight: "bold", marginLeft: "5px" }}>
                  {orderDetails.orderStatus === "delivered"
                    ? new Date(orderDetails.updatedAt).toLocaleDateString("en-US", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })
                    : "Apr 25, 2025"}
                </span>
              </p>
              {orderDetails.orderStatus === "pending" && (
                <button
                  style={{ ...styles.makeReadyButton, opacity: actionLoading ? 0.8 : 1 }}
                  onClick={() => setIsConfirmModalOpen(true)}
                  disabled={!!actionLoading}
                >
                  Mark As Confirmed
                </button>
              )}
              {orderDetails.orderStatus === "confirmed" && (
                <button
                  style={{ ...styles.makeReadyButton, opacity: actionLoading ? 0.8 : 1 }}
                  onClick={() => updateOrderStatus("processing", "", "", "processing")}
                  disabled={!!actionLoading}
                >
                  {renderActionLabel("processing", "Mark As Processing")}
                </button>
              )}
              {orderDetails.orderStatus === "processing" && (
                <button
                  style={{ ...styles.makeReadyButton, opacity: actionLoading ? 0.8 : 1 }}
                  onClick={() => updateOrderStatus("outForDelivery", "", "", "outForDelivery")}
                  disabled={!!actionLoading}
                >
                  {renderActionLabel("outForDelivery", "Mark As Out for Delivery")}
                </button>
              )}
              {orderDetails.orderStatus === "outForDelivery" && (
                <button
                  style={{ ...styles.makeReadyButton, backgroundColor: "#4CAF50", opacity: actionLoading ? 0.8 : 1 }}
                  onClick={() => updateOrderStatus("delivered", "", "", "delivered")}
                  disabled={!!actionLoading}
                >
                  {renderActionLabel("delivered", "Mark As Delivered", "#fff")}
                </button>
              )}
            </div>
          </div>
          <div style={styles.card}>
            <h2 style={styles.cardHeader}>Product Information</h2>
            <div style={styles.productTableWrapper}>
              <table style={styles.productTable}>
                <thead style={styles.productTableHeader}>
                  <tr>
                    <th style={styles.productTh}>Product Name</th>
                    <th style={styles.productTh}>QTY</th>
                    <th style={styles.productTh}>PRICE</th>
                    <th style={styles.productTh}>Tax</th>
                    <th style={styles.productTh}>AMOUNT</th>
                    {orderDetails.orderStatus === "delivered" && (
                      <th style={styles.productTh}>REVIEW LINK</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {orderDetails.items?.map((item, index) => {
                    const reviewLink = `${window.location.origin}/review?orderId=${orderDetails._id}&userId=${orderDetails.customerId?._id}&productId=${item.productId?._id}&productName=${encodeURIComponent(item.productId?.name || 'Product')}`;
                    return (
                      <tr key={item.productId._id || index} style={styles.productRow}>
                        <td style={styles.productTd}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            {item.productId?.images?.[0] && (
                              <div style={styles.productIconContainer}>
                                <img
                                  src={item.productId.images[0] || "/placeholder.svg"}
                                  alt="Product"
                                  style={styles.productIconImage}
                                />
                              </div>
                            )}
                            {item.productId?.name || "Product name not available"}
                          </div>
                        </td>
                        <td style={styles.productTd}>{item.quantity || 0}</td>
                        <td style={styles.productTd}>{item.priceAtPurchase || 0}</td>
                        <td style={styles.productTd}>{item.tax || "N/A"}</td>
                        <td style={styles.productTd}>
                          {item.quantity && item.priceAtPurchase
                            ? (item.quantity * item.priceAtPurchase).toFixed(2)
                            : "N/A"}
                        </td>
                        {orderDetails.orderStatus === "delivered" && (
                          <td style={styles.productTd}>
                            <button
                              onClick={() => copyToClipboard(reviewLink, item.productId?._id)}
                              style={{
                                padding: '8px 14px',
                                backgroundColor: copiedLinkIndex === item.productId?._id ? '#4CAF50' : '#D4AF37',
                                border: 'none',
                                borderRadius: '6px',
                                color: copiedLinkIndex === item.productId?._id ? '#fff' : '#000',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontWeight: '600',
                                fontSize: '12px',
                                transition: 'background-color 0.3s',
                              }}
                            >
                              <Copy size={14} />
                              {copiedLinkIndex === item.productId?._id ? 'Copied!' : 'Copy Link'}
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div style={styles.column}>
          <div style={styles.card}>
            <h2 style={styles.cardHeader}>Order Summary</h2>
            <div style={styles.orderSummaryRow}>
              <span style={styles.orderSummaryLabel}>
                <FileText style={styles.orderSummaryIcon} />
                Sub Total:
              </span>
              <span style={styles.orderSummaryValue}>{subTotal.toFixed(2)}</span>
            </div>
            <div style={styles.orderSummaryDivider}></div>
            <div style={styles.orderSummaryRow}>
              <span style={styles.orderSummaryLabel}>
                <Percent style={styles.orderSummaryIcon} />
                Discount:
              </span>
              <span style={styles.orderSummaryValue}>-{discount.toFixed(2)}</span>
            </div>
            <div style={styles.orderSummaryDivider}></div>
            <div style={styles.orderSummaryRow}>
              <span style={styles.orderSummaryLabel}>
                <Truck style={styles.orderSummaryIcon} />
                Delivery Charges:
              </span>
              <span style={styles.orderSummaryValue}>{deliveryCharges.toFixed(2)}</span>
            </div>
            <div style={styles.orderSummaryDivider}></div>
            <div style={styles.orderSummaryRow}>
              <span style={styles.orderSummaryLabel}>
                <FileSpreadsheet style={styles.orderSummaryIcon} />
                Estimated Tax(15.5%):
              </span>
              <span style={styles.orderSummaryValue}>{estimatedTax.toFixed(2)}</span>
            </div>
            <div style={styles.orderSummaryDivider}></div>
            <div style={styles.orderSummaryRow}>
              <span style={{ ...styles.orderSummaryLabel, ...styles.totalAmount, gap: "0" }}>Total Amount</span>
              <span style={{ ...styles.orderSummaryValue, ...styles.totalAmountValue }}>
                {orderDetails.totalAmount?.toFixed(2) || (subTotal + estimatedTax).toFixed(2)}
              </span>
            </div>
          </div>
          <div style={styles.card}>
            <h2 style={styles.paymentInfoCardHeader}>Payment Information</h2>
            <hr style={styles.paymentInfoDivider} />
            {orderDetails.paymentMethod === "COD" ? (
              <>
                <div style={styles.paymentInfoRow}>
                  <div style={styles.paymentIconCircle}>
                    <CreditCard style={styles.paymentInfoIcon} />
                  </div>
                  <span style={styles.paymentInfoLabel}>Payment Method:</span>
                  <span style={styles.paymentInfoValueBold}>Cash on Delivery</span>
                </div>
                <div style={styles.paymentInfoRow}>
                  <div style={styles.paymentIconCircle}>
                    <CircleDollarSign style={styles.paymentInfoIcon} />
                  </div>
                  <span style={styles.paymentInfoLabel}>Status:</span>
                  <span style={styles.paymentInfoValue}>
                    {orderDetails.paymentStatus === "paid" ? "Paid" : "Unpaid"}
                  </span>

                </div>
              </>
            ) : (
              <>
                <div style={styles.paymentInfoRow}>
                  <div style={styles.paymentIconCircle}>
                    <CreditCard style={styles.paymentInfoIcon} />
                  </div>
                  <span style={styles.paymentInfoLabel}>Payment Method:</span>
                  <span style={styles.paymentInfoValueBold}>Bank Transfer</span>
                </div>
                <div style={styles.paymentInfoRow}>
                  <div style={styles.paymentIconCircle}>
                    <CheckCircle style={{ ...styles.paymentInfoIcon, color: "#4CAF50" }} />
                  </div>
                  <span style={styles.paymentInfoLabel}>Payment Status:</span>
                  <span style={{ ...styles.paymentInfoValueBold, color: "#4CAF50" }}>Paid & Verified</span>
                </div>
                <div style={styles.paymentInfoRow}>
                  <div style={styles.paymentIconCircle}>
                    <CircleDollarSign style={styles.paymentInfoIcon} />
                  </div>
                  <span style={styles.paymentInfoLabel}>Amount Received:</span>
                  <span style={styles.paymentInfoValueBold}>PKR {orderDetails.totalAmount?.toFixed(2) || "0.00"}</span>
                </div>
              </>
            )}
          </div>
          {orderDetails.affiliateId && (
            <div style={styles.card}>
              <h2 style={styles.cardHeader}>Affiliate Attribution</h2>
              <div style={styles.orderSummaryRow}>
                <span style={styles.orderSummaryLabel}>Affiliate:</span>
                <span style={styles.orderSummaryValue}>
                  {orderDetails.affiliateId?.name || "—"}
                </span>
              </div>
              <div style={styles.orderSummaryRow}>
                <span style={styles.orderSummaryLabel}>Code:</span>
                <span style={styles.orderSummaryValue}>
                  {orderDetails.affiliateCode || orderDetails.affiliateId?.code || "—"}
                </span>
              </div>
              <div style={styles.orderSummaryRow}>
                <span style={styles.orderSummaryLabel}>Commission Rate:</span>
                <span style={styles.orderSummaryValue}>
                  {(orderDetails.commissionRate ?? orderDetails.affiliateId?.commissionRate ?? 0)}%
                </span>
              </div>
              <div style={styles.orderSummaryRow}>
                <span style={styles.orderSummaryLabel}>Commission Amount:</span>
                <span style={styles.orderSummaryValue}>
                  PKR {(orderDetails.commissionAmount || 0).toFixed(2)}
                </span>
              </div>
              <div style={styles.orderSummaryRow}>
                <span style={styles.orderSummaryLabel}>Commission Status:</span>
                <span style={styles.orderSummaryValue}>
                  {orderDetails.commissionStatus || "none"}
                </span>
              </div>
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/affiliates/details/${orderDetails.affiliateId?._id || orderDetails.affiliateId}`
                  )
                }
                style={{
                  marginTop: "12px",
                  padding: "8px 14px",
                  background: "#000",
                  color: "#E5D0A5",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                View Affiliate Profile
              </button>
            </div>
          )}
          <div style={styles.card}>
            <h2 style={styles.cardHeader}>Contact Number</h2>
            <p style={styles.addressCardText}>
              <Phone style={{ marginRight: "5px", color: "#D4AF37" }} size={16} />
              {orderDetails.shippingAddress?.phoneNumber || "+92-xxx-xxxx-xxx"}
              <Edit style={styles.addressEditIcon} size={14} />
            </p>
            <p style={styles.addressCardText}>
              <Mail style={{ marginRight: "5px", color: "#D4AF37" }} size={16} />
              {orderDetails.customerId?.email || "email@example.com"}
              <Edit style={styles.addressEditIcon} size={14} />
            </p>
          </div>
          <div style={styles.card}>
            <OrderMap city={orderDetails?.shippingAddress?.city || "Sargodha"} />
          </div>
        </div>
      </div>
      {isReturnModalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <XCircle
              style={styles.modalClose}
              size={20}
              onClick={() => {
                setIsReturnModalOpen(false)
                setReturnReason("")
                setReturnError("")
              }}
            />
            <h2 style={styles.modalHeader}>Return Order</h2>
            <textarea
              style={styles.modalInput}
              placeholder="Enter reason for return"
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              rows={4}
            />
            {returnError && <p style={styles.modalError}>{returnError}</p>}
            <button
              style={{ ...styles.modalButton, opacity: actionLoading ? 0.8 : 1 }}
              onClick={handleReturnSubmit}
              disabled={!!actionLoading}
            >
              {renderActionLabel("returned", "Submit Return")}
            </button>
          </div>
        </div>
      )}
      {isConfirmModalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <XCircle
              style={styles.modalClose}
              size={20}
              onClick={() => {
                setIsConfirmModalOpen(false)
                setCourierService("")
                setConfirmError("")
              }}
            />
            <h2 style={styles.modalHeader}>Confirm Order</h2>
            <select
              style={styles.modalInput}
              value={courierService}
              onChange={(e) => setCourierService(e.target.value)}
            >
              <option value="">Select Courier Service</option>
              <option value="Leopards">Leopards</option>
              <option value="BlueEx">PostEx</option>
              <option value="M&P">M&P</option>
           
            </select>
            {confirmError && <p style={styles.modalError}>{confirmError}</p>}
            <button
              style={{ ...styles.modalButton, opacity: actionLoading ? 0.8 : 1 }}
              onClick={handleConfirmSubmit}
              disabled={!!actionLoading}
            >
              {renderActionLabel("confirmed", "Submit Confirmation")}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}